/*
 * Customer Ratings API client.
 *
 *   POST /api/customer/restaurants/{restaurantId}/rate
 *        Body: { rating: 1-5, comment?, order_id? }
 *        Müşteri restoranı puanlar. order_id verilirse o ziyarete bağlanır.
 *
 *   POST /api/customer/waiters/{waiterId}/rate
 *        Body: { rating: 1-5, comment?, order_id }
 *        Müşteri ofisantı puanlar. order_id ZORUNLU.
 *
 *   GET  /api/customer/ratings/mine
 *        Müşterinin verdiği TÜM puanlar (restoran + ofisant), son veriliş
 *        en üstte.
 *
 * Auth: Bearer customer Sanctum token (customerClient interceptor ekler).
 */

import { customerClient } from './customerAuth';

export interface RatingPayload {
  rating: number; // 1-5
  comment?: string | null;
  order_id?: number | null;
}

export interface RestaurantRatingPayload extends RatingPayload {
  // restaurant rate'inde order_id opsiyonel.
}

export interface WaiterRatingPayload extends RatingPayload {
  order_id: number; // waiter rate'inde zorunlu
}

export interface CustomerRating {
  id: number;
  type: 'restaurant' | 'waiter';
  rating: number;
  comment: string | null;
  created_at: string;
  /** Restoran adı + logo veya ofisant adı. Backend'in dönüş şekline göre esnek. */
  target?: {
    id: number;
    name: string | null;
    logo?: string | null;
    avatar?: string | null;
  } | null;
  restaurant?: {
    id: number;
    name: string | null;
    logo?: string | null;
  } | null;
  waiter?: {
    id: number;
    name: string | null;
    avatar?: string | null;
  } | null;
  order?: {
    id: number;
    order_number?: string | null;
    created_at?: string | null;
  } | null;
  [extra: string]: any;
}

const normalizeListResponse = (data: any): CustomerRating[] => {
  if (Array.isArray(data)) return data as CustomerRating[];
  if (Array.isArray(data?.data)) return data.data as CustomerRating[];
  if (Array.isArray(data?.ratings)) return data.ratings as CustomerRating[];
  return [];
};

/** Restoranı puanla. order_id verilirse o ziyarete bağlanır (idempotent). */
export async function rateRestaurant(
  restaurantId: number,
  payload: RestaurantRatingPayload,
): Promise<CustomerRating> {
  const res = await customerClient.post(
    `/customer/restaurants/${restaurantId}/rate`,
    payload,
  );
  // Bazı uçlar `{ rating: {...} }` döner — dış sarmalayıcıyı toleransla aç.
  const data: any = res.data;
  if (data && typeof data === 'object' && 'rating' in data && data.rating && typeof data.rating === 'object') {
    return data.rating as CustomerRating;
  }
  return data as CustomerRating;
}

/** Ofisantı puanla. order_id ZORUNLU. */
export async function rateWaiter(
  waiterId: number,
  payload: WaiterRatingPayload,
): Promise<CustomerRating> {
  const res = await customerClient.post(
    `/customer/waiters/${waiterId}/rate`,
    payload,
  );
  const data: any = res.data;
  if (data && typeof data === 'object' && 'rating' in data && data.rating && typeof data.rating === 'object') {
    return data.rating as CustomerRating;
  }
  return data as CustomerRating;
}

/** Müşterinin verdiği tüm puanlar (restoran + ofisant), son veriliş üstte. */
export async function getMyRatings(signal?: AbortSignal): Promise<CustomerRating[]> {
  const res = await customerClient.get('/customer/ratings/mine', { signal });
  return normalizeListResponse(res.data);
}
